/**
 * The BYBLOCK child context a block REFERENCE hands to its content — a LEAF
 * module (its only dependencies are the `AdapterContext` type, the colour
 * resolver and the leaf linetype resolver).
 *
 * # What it is
 *
 * AutoCAD resolves a `ByBlock` colour / linetype of an entity inside a block
 * from the BLOCK REFERENCE that draws it, not from the entity's layer and not
 * from whatever reference happens to enclose that one. So every reference has
 * to publish its OWN resolved appearance before flattening its content:
 * `blockColor`, `blockLinetype` and the accumulated group-48 linetype scale
 * (`buildRenderableShellTyped` / `resolveLinetypeName` in
 * `../adapterContext.ts` read exactly these three).
 *
 * # Why it is shared (@codex PR #785 round 2, F2)
 *
 * Three reference kinds need the identical thread and MUST NOT drift:
 *
 *   - the TOP-LEVEL INSERT (`./blockFromEntity.ts`),
 *   - a NESTED INSERT child (same file, the recursion arm),
 *   - an `ACAD_TABLE`, which IS a block reference to its anonymous `*T<N>`
 *     graphics block (`./tableFromEntity.ts`). Before this module the table
 *     forwarded the ambient context verbatim, so a table with an explicit
 *     colour or linetype drew its ByBlock grid / cell graphics from the LAYER
 *     (or from an enclosing INSERT) instead of from the table reference.
 *
 * `flattenBlockReference` deliberately does NOT thread this itself — its
 * documented parity contract with the deleted legacy low-level entry is
 * "ctx verbatim; callers that need a thread pass it in `ctx` themselves"
 * (see its doc comment in `./blockFromEntity.ts`). This module is that
 * "themselves", written once.
 */

import type { DrawingEntity } from "@dxf-ai-editor/drawing-model";

import { resolveColorHexFromEntity } from "../../rendering/colorResolution";

import { entityForegroundHex, type AdapterContext } from "../adapterContext";

import { resolveInsertLinetypeTyped } from "./insertLinetype";

/**
 * The appearance fields of a block REFERENCE that its content's BYBLOCK chain
 * consults. A `BlockInsertEntity` and a `TableEntity` both satisfy it
 * structurally; the nested-INSERT arm builds one explicitly because its
 * `layer` must be the EFFECTIVE layer (a nested INSERT authored on "0" sits on
 * the inherited one — @codex #431 round-3), not the authored field.
 */
export interface BlockReferenceAppearance {
  color: DrawingEntity["color"];
  /** EFFECTIVE layer of the reference (what ByLayer resolves against). */
  layer: string;
  linetype?: string;
  /** DXF group 48. */
  linetypeScale?: number;
  /**
   * The reference's own space (`DrawingEntity["spaceId"]`) — the background its
   * ink is drawn on, and therefore what its ACI 7 resolves to. A layout pass
   * carries model-space INSERTs (what its viewports show) alongside the
   * sheet's own, so the reference's RESOLVED `blockColor` — which every
   * ByBlock child inherits — has to be resolved on ITS space, not the pass's
   * (`rendering/layoutForeground.ts`; @codex round 15 P1). A nested INSERT
   * inherits the enclosing reference's space, exactly like its `layer`.
   */
  spaceId: string;
}

/**
 * PR-LT3 clamp for a TYPED group-48 value (the numeric twin of
 * `parseLinetypeScale`'s string-path clamp): missing / non-finite /
 * non-positive → 1.
 */
function clampLinetypeScale(value: number | undefined): number {
  return typeof value === "number" && Number.isFinite(value) && value > 0
    ? value
    : 1;
}

/**
 * Derive the context a block reference's CONTENT is flattened with.
 *
 *   - `blockColor` — the reference's own RESOLVED colour hex, so a BYBLOCK
 *     child picks it up (`resolveColorHexFromEntity` walks the whole chain:
 *     explicit → its hex; ByLayer → the reference's layer hex; ByBlock → the
 *     ENCLOSING `ctx.blockColor`).
 *   - `blockLinetype` — the reference's own resolved linetype NAME, same
 *     chain (`resolveInsertLinetypeTyped`); `null` for Continuous, so a
 *     ByBlock child degrades to a solid line.
 *   - `blockLinetypeScale` — the enclosing accumulated group-48 scale TIMES
 *     this reference's own (dash lengths multiply down the chain).
 *
 * Every other field of `ctx` (layer tables, camera zoom, block definitions,
 * annotation scale …) rides through untouched.
 */
export function blockReferenceChildContext(
  reference: BlockReferenceAppearance,
  ctx: AdapterContext,
): AdapterContext {
  return {
    ...ctx,
    blockColor: resolveColorHexFromEntity(
      { color: reference.color, layer: reference.layer },
      ctx.layerColors,
      ctx.blockColor ?? null,
      entityForegroundHex(reference, ctx),
    ),
    blockLinetype: resolveInsertLinetypeTyped(
      reference.linetype,
      reference.layer,
      ctx,
    ),
    blockLinetypeScale:
      (ctx.blockLinetypeScale ?? 1) * clampLinetypeScale(reference.linetypeScale),
  };
}
